import { useEffect, useState } from "react";
import AdminLayout from "../layout/AdminLayout";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../../config/api";
import { adminAuthHeader } from "../../utils/authHeader";

const AdminOwnerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [owner,   setOwner]   = useState(null);
  const [frauds,  setFrauds]  = useState([]);

  // Delete modal state
  const [deleteModal,   setDeleteModal]   = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [deleteReports, setDeleteReports] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError,   setDeleteError]   = useState("");
  const [showPassword,  setShowPassword]  = useState(false);

  const fetchOwnerDetails = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/admin/owners/${id}`,
        { headers: adminAuthHeader() }
      );
      setOwner(res.data.owner);
      setFrauds(res.data.frauds);
    } catch (err) {
      console.error("Failed to load owner details", err);
    }
  };

  useEffect(() => { fetchOwnerDetails(); }, []);

  const toggleBlock = async () => {
    try {
      const url = owner.user.isBlocked
        ? `${API_BASE_URL}/api/admin/owners/${owner.user._id}/unblock`
        : `${API_BASE_URL}/api/admin/owners/${owner.user._id}/block`;
      await axios.put(url, {}, { headers: adminAuthHeader() });
      fetchOwnerDetails();
    } catch { console.error("Block/Unblock failed"); }
  };

  const handleDelete = async () => {
    if (!adminPassword.trim()) { setDeleteError("Please enter your admin password."); return; }
    setDeleteLoading(true);
    setDeleteError("");
    try {
      const res = await axios.delete(
        `${API_BASE_URL}/api/admin/owners/${owner.user._id}`,
        {
          headers: { ...adminAuthHeader(), "Content-Type": "application/json" },
          data: { adminPassword, deleteReports },
        }
      );
      alert("✅ " + res.data.message);
      navigate("/admin/owners");
    } catch (err) {
      setDeleteError(err.response?.data?.message || "Delete failed. Try again.");
    }
    setDeleteLoading(false);
  };

  if (!owner) {
    return (
      <AdminLayout>
        <div style={{ textAlign: "center", padding: 60, color: "#a3aed0" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
          Loading owner details…
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-owner-page">

        {/* ── HEADER with Back + Delete ── */}
        <div className="admin-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            {/* Back button */}
            <button
              onClick={() => navigate("/admin/owners")}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                background: "#f4f7fe", border: "1.5px solid #e0e5f2",
                borderRadius: 8, padding: "7px 14px", cursor: "pointer",
                fontSize: 13, fontWeight: 600, color: "#4318FF",
                marginBottom: 10,
              }}>
              ← Back to Owners
            </button>
            <p className="admin-welcome">Owner Details</p>
            <h2 className="admin-title">{owner.companyName}</h2>
          </div>

          {/* Delete button */}
          <button
            onClick={() => { setDeleteModal(true); setAdminPassword(""); setDeleteError(""); setDeleteReports(false); }}
            style={{
              padding: "10px 20px", background: "#fee2e2", color: "#dc2626",
              border: "1.5px solid #fecaca", borderRadius: 10,
              fontWeight: 700, fontSize: 13, cursor: "pointer",
              alignSelf: "flex-end",
            }}>
            🗑️ Delete Owner
          </button>
        </div>

        {/* ── OWNER PROFILE CARD ── */}
        <div className="admin-card admin-owner-detail-card owner-summary">
          <div className="admin-owner-left">
            <div className="admin-owner-avatar">
              <img
                src={
  owner.user.profilePhoto
    ? owner.user.profilePhoto.startsWith("http")
      ? owner.user.profilePhoto
      : `${API_BASE_URL}${owner.user.profilePhoto}`
    : owner.user.profileImage
      ? `${API_BASE_URL}${owner.user.profileImage}`
      : "https://via.placeholder.com/72"
}
                alt="Owner"
              />
            </div>
            <div className="admin-owner-text">
              <h3>{owner.ownerName}</h3>
              <p className="admin-company">{owner.companyName}</p>
              <div className="admin-owner-meta">
                <span>📍 {owner.city}, {owner.district}, {owner.state}</span>
                <span>📧 {owner.user?.email || owner.email}</span>
                <span>📞 +91 {owner.phone}</span>
              </div>
            </div>
          </div>

          <div className="admin-owner-right">
            <span className={`status ${
  owner.user.isBlocked ? "rejected" :
  owner.user.subscription?.status === "active" ? "approved" :
  owner.user.subscription?.status === "pending" ? "pending" : "rejected"
}`}>
  {owner.user.isBlocked ? "Blocked" :
   owner.user.subscription?.status === "active" ? "Active" :
   owner.user.subscription?.status === "pending" ? "Pending Payment" :
   owner.user.subscription?.status === "trial"   ? "Pending Activation" : "Inactive"}
</span>
            <button className="reject-btn" onClick={toggleBlock}>
              {owner.user.isBlocked ? "Unblock Owner" : "Block Owner"}
            </button>
          </div>
        </div>

        {/* ── FRAUD LIST ── */}
        <div className="admin-card">
          <h3 className="admin-section-title">
            Fraud Reports by this Owner
            {frauds.length > 0 && (
              <span style={{ fontSize: 13, fontWeight: 500, color: "#a3aed0", marginLeft: 8 }}>
                ({frauds.length} total)
              </span>
            )}
          </h3>

          {frauds.length === 0 && (
            <p style={{ color: "#a3aed0", fontSize: 13, padding: "20px 0" }}>No fraud reports found.</p>
          )}

          {frauds.map((fraud) => (
            <div key={fraud._id} className="admin-fraud-item">
              <div className="fraud-left">
                <h4>{fraud.fraudType}</h4>
                <p>{fraud.fraudCity}</p>
                <span className="fraud-date">
                  {new Date(fraud.fraudDate || fraud.createdAt).toLocaleDateString("en-IN")}
                </span>
              </div>
              <div className="fraud-right">
                <span className={`status ${fraud.status}`}>{fraud.status}</span>
                <button className="view-btn" onClick={() => navigate(`/admin/frauds/${fraud._id}`)}>
                  View
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── SECURE DELETE MODAL ── */}
      {deleteModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
        }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 440, maxWidth: "95vw" }}>

            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 48, marginBottom: 8 }}>⚠️</div>
              <h3 style={{ margin: 0, color: "#dc2626", fontSize: 18 }}>Delete Owner Account</h3>
              <p style={{ color: "#6b7280", fontSize: 13, marginTop: 6 }}>
                You are about to permanently delete <b>{owner.ownerName}</b> ({owner.companyName}).<br/>
                This action cannot be undone.
              </p>
            </div>

            {/* What to do with fraud reports */}
            <div style={{ background: "#f9faff", borderRadius: 10, padding: 14, marginBottom: 16 }}>
              <p style={{ margin: "0 0 10px", fontWeight: 700, fontSize: 13, color: "#1b2559" }}>
                What to do with their fraud reports?
              </p>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 8 }}>
                <input type="radio" name="delReports" checked={deleteReports === false} onChange={() => setDeleteReports(false)} />
                <span style={{ fontSize: 13, color: "#374151" }}>
                  <b>Keep reports</b> — fraud data stays visible to other owners
                </span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <input type="radio" name="delReports" checked={deleteReports === true} onChange={() => setDeleteReports(true)} />
                <span style={{ fontSize: 13, color: "#dc2626" }}>
                  <b>Delete all reports</b> — permanently removes all their fraud records
                </span>
              </label>
            </div>

            {/* Admin Password */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#1b2559", marginBottom: 6 }}>
                🔐 Enter your admin password to confirm
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Your admin password"
                  value={adminPassword}
                  onChange={e => { setAdminPassword(e.target.value); setDeleteError(""); }}
                  onKeyDown={e => e.key === "Enter" && handleDelete()}
                  autoFocus
                  style={{ width: "100%", padding: "10px 44px 10px 12px", borderRadius: 8, border: "1.5px solid #e0e5f2", fontSize: 13, outline: "none", boxSizing: "border-box" }}
                />
                <button onClick={() => setShowPassword(!showPassword)}
                  style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: 16 }}>
                  {showPassword ? "🙈" : "👁️"}
                </button>
              </div>
              {deleteError && (
                <p style={{ color: "#dc2626", fontSize: 12, marginTop: 6, fontWeight: 600 }}>❌ {deleteError}</p>
              )}
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setDeleteModal(false)}
                style={{ padding: "10px 20px", borderRadius: 8, border: "1.5px solid #e0e5f2", background: "#f4f7fe", cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleteLoading || !adminPassword.trim()}
                style={{
                  padding: "10px 22px", borderRadius: 8, border: "none", cursor: "pointer",
                  fontWeight: 700, fontSize: 13, color: "#fff",
                  background: !adminPassword.trim() ? "#a3aed0" : "#dc2626",
                  opacity: deleteLoading ? 0.7 : 1,
                }}>
                {deleteLoading ? "Deleting…" : "🗑️ Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminOwnerDetails;